import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@artosera/shared';
import { Button, Dialog, Input, Price, Select, Textarea } from '@artosera/ui';
import { useContacts } from '../../api/contacts.js';
import { appelDeRequete, cleMetier, dansLaPortee } from '../../api/cles.js';
import { useMutation } from '../../api/mutation.js';
import {
  presentationApi,
  type CalculNegociation,
  type DemandeApprobation,
  type EntreeCalcul,
} from '../../api/presentation.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { Field } from '../Field.js';
import { useDebounce } from '../oeuvres/useDebounce.js';
import { useEnLigne } from '../racine/BandeauHorsLigne.js';
import { useShellContext } from '../shell/context.js';
import { ErreurPresentation } from './ErreurPresentation.js';
import styles from './Presentation.module.css';

export interface CibleNegociation {
  oeuvreId: string;
  exemplaireId?: string | null;
  devise: string;
  contactId?: string | null;
  selectionId?: string;
}

export function BoutonCalculette(cible: CibleNegociation) {
  const { galerie, galerieSlug, compte } = useShellContext();
  const { t } = useTranslation('presentation');
  const [ouverte, setOuverte] = useState(false);
  if (
    !galerieAModule(galerie, 'rendez-vous-client') ||
    !galerieALaction(galerie, 'presentation:read')
  )
    return null;
  return (
    <>
      <Button variant="secondary" onClick={() => setOuverte(true)}>
        {t('calculette.titre')}
      </Button>
      {ouverte ? (
        <CalculetteNegociation
          key={`${compte.id}:${galerieSlug}:${cible.oeuvreId}:${cible.exemplaireId ?? ''}`}
          {...cible}
          onFermer={() => setOuverte(false)}
        />
      ) : null}
    </>
  );
}

export function CalculetteNegociation({
  onFermer,
  ...cible
}: CibleNegociation & { onFermer: () => void }) {
  const { galerie } = useShellContext();
  if (
    !galerieAModule(galerie, 'rendez-vous-client') ||
    !galerieALaction(galerie, 'presentation:read')
  )
    return null;
  return <DialogueCalculette {...cible} onFermer={onFermer} />;
}

function DialogueCalculette({
  oeuvreId,
  exemplaireId,
  devise,
  contactId: contactInitial,
  selectionId,
  onFermer,
}: CibleNegociation & { onFermer: () => void }) {
  const { t, i18n } = useTranslation('presentation');
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const { galerieSlug, galerie } = useShellContext();
  const enLigne = useEnLigne();
  const [mode, setMode] = useState('prix');
  const [saisie, setSaisie] = useState('');
  const [contactId, setContactId] = useState(contactInitial ?? '');
  const [recherche, setRecherche] = useState('');
  const [motif, setMotif] = useState('');
  const rechercheDebattue = useDebounce(recherche);
  const peutDemander = galerieALaction(galerie, 'presentation:write');
  const contacts = useContacts(
    galerieSlug,
    { q: rechercheDebattue, parPage: 20 },
    {
      enabled:
        enLigne &&
        peutDemander &&
        !contactInitial &&
        galerieALaction(galerie, 'contacts:read') &&
        rechercheDebattue.trim().length >= 2,
    },
  );
  const entree = useMemo<EntreeCalcul | null>(() => {
    const base = { oeuvreId, exemplaireId, ...(contactId ? { contactId } : {}) };
    if (!saisie.trim()) return base;
    if (!/^\d+(?:[.,]\d{1,2})?$/.test(saisie)) return null;
    const valeur = Math.round(Number(saisie.replace(',', '.')) * 100);
    if (!Number.isSafeInteger(valeur) || (mode === 'pourcentage' && valeur > 10000)) return null;
    if (mode === 'pourcentage') return { ...base, remisePointsDeBase: valeur };
    if (mode === 'remise') return { ...base, remiseCentimes: valeur };
    return { ...base, prixProposeCentimes: valeur };
  }, [oeuvreId, exemplaireId, contactId, saisie, mode]);
  const debattue = useDebounce(entree);
  const calcul = useQuery({
    queryKey: cleMetier(galerieSlug, 'presentation', 'calcul', debattue),
    queryFn: (contexte) => {
      if (!debattue) throw new Error('Entrée de calcul absente.');
      return presentationApi.calculer(galerieSlug, debattue, appelDeRequete(contexte));
    },
    enabled: enLigne && entree !== null && entree === debattue,
    staleTime: 0,
  });
  const demande = useMutation(
    dansLaPortee({
      mutationFn: (operation: { corps: DemandeApprobation; cleOperation?: string }, appel) => {
        operation.cleOperation ??= crypto.randomUUID();
        return presentationApi.demander(galerieSlug, operation.corps, {
          ...appel,
          cleOperation: operation.cleOperation,
        });
      },
    }),
  );
  const resultat = enLigne && entree === debattue ? calcul.data?.calcul : undefined;
  const demandeCourante = demande.variables?.corps;
  const demandeCorrespond =
    demandeCourante?.contactId === contactId &&
    demandeCourante?.prixProposeCentimes === resultat?.prixProposeCentimes;
  const suivi = useQuery({
    queryKey: cleMetier(
      galerieSlug,
      'presentation',
      'suivi-approbation',
      demande.data?.approbation.id,
    ),
    queryFn: (contexte) =>
      presentationApi.approbations(galerieSlug, { oeuvreId, contactId }, appelDeRequete(contexte)),
    enabled: enLigne && demandeCorrespond && Boolean(demande.data),
    refetchInterval: (requete) => {
      const etat = requete.state.data?.approbations.find(
        (accord) => accord.id === demande.data?.approbation.id,
      )?.statut;
      return !etat || etat === 'en_attente' ? 5000 : false;
    },
  });
  const approbation =
    resultat?.approbation ??
    (demandeCorrespond
      ? (suivi.data?.approbations.find((accord) => accord.id === demande.data?.approbation.id) ??
        demande.data?.approbation)
      : undefined);
  return (
    <Dialog
      open
      onOpenChange={(ouverte) => {
        if (!ouverte) onFermer();
      }}
      title={t('calculette.titre')}
      description={t('calculette.description')}
      closeLabel={t('mode.fermer')}
    >
      {!enLigne ? (
        <p role="status">{t('calculette.reseau')}</p>
      ) : (
        <div className={styles.formulaire}>
          <Field htmlFor="negociation-mode" label={t('calculette.saisie')}>
            <Select
              id="negociation-mode"
              value={mode}
              onValueChange={(valeur) => {
                setMode(valeur);
                setSaisie('');
              }}
              options={[
                { value: 'prix', label: t('calculette.prixProposeCentimes') },
                { value: 'pourcentage', label: t('calculette.pourcentage') },
                { value: 'remise', label: t('calculette.remiseCentimes') },
              ]}
            />
          </Field>
          <Field htmlFor="negociation-valeur" label={t('calculette.valeur')}>
            <Input
              id="negociation-valeur"
              inputMode="decimal"
              value={saisie}
              onChange={(evenement) => setSaisie(evenement.target.value)}
              aria-invalid={entree === null}
            />
          </Field>
          {entree === null ? (
            <p role="alert" className={styles.erreur}>
              {t('calculette.invalide')}
            </p>
          ) : null}
          {calcul.isFetching || entree !== debattue ? <p role="status">{t('chargement')}</p> : null}
          <ErreurPresentation
            erreur={calcul.error ?? demande.error ?? contacts.error ?? suivi.error}
            recharger={() => {
              if (demande.isError && demande.variables) demande.mutate(demande.variables);
              else if (suivi.isError) void suivi.refetch();
              else void calcul.refetch();
            }}
          />
          {resultat ? <ResultatCalcul calcul={resultat} devise={devise} /> : null}
          {resultat?.approbationRequise && !approbation ? (
            <p role="status">{t('calculette.approbationRequise')}</p>
          ) : null}
          {approbation ? (
            <div role="status">
              <p>{t('calculette.etat', { etat: t(`approbations.${approbation.statut}`) })}</p>
              {approbation.expireLe ? (
                <p>
                  {t('calculette.expiration', {
                    date: formatDate(new Date(approbation.expireLe), langue, 'court'),
                  })}
                </p>
              ) : null}
              {approbation.decision?.note ? <p>{approbation.decision.note}</p> : null}
            </div>
          ) : null}
          {resultat?.approbationRequise &&
          !resultat.approbation &&
          peutDemander &&
          approbation?.statut !== 'acceptee' &&
          approbation?.statut !== 'en_attente' ? (
            <form
              className={styles.formulaire}
              onSubmit={(evenement) => {
                evenement.preventDefault();
                if (!contactId || !resultat || entree !== debattue || demande.isPending) return;
                demande.mutate({
                  corps: {
                    oeuvreId,
                    exemplaireId,
                    contactId,
                    ...(contactInitial && selectionId ? { selectionId } : {}),
                    prixProposeCentimes: resultat.prixProposeCentimes,
                    motifDemande: motif,
                  },
                });
              }}
            >
              {!contactInitial ? (
                <>
                  <Field htmlFor="negociation-recherche" label={t('rechercheContact')}>
                    <Input
                      id="negociation-recherche"
                      type="search"
                      value={recherche}
                      onChange={(evenement) => setRecherche(evenement.target.value)}
                      disabled={!galerieALaction(galerie, 'contacts:read')}
                    />
                  </Field>
                  <Field htmlFor="negociation-contact" label={t('contact')}>
                    <Select
                      id="negociation-contact"
                      value={contactId}
                      onValueChange={setContactId}
                      options={(contacts.data?.contacts ?? [])
                        .filter((contact) => !contact.archive && !contact.anonymise)
                        .map((contact) => ({ value: contact.id, label: contact.nomAffiche }))}
                    />
                  </Field>
                  {!contactId ? <p>{t('calculette.contactRequis')}</p> : null}
                </>
              ) : null}
              <Field htmlFor="negociation-motif" label={t('calculette.motif')}>
                <Textarea
                  id="negociation-motif"
                  maxLength={1000}
                  value={motif}
                  onChange={(evenement) => setMotif(evenement.target.value)}
                />
              </Field>
              <Button
                type="submit"
                loading={demande.isPending}
                disabled={!contactId || entree !== debattue || calcul.isFetching}
              >
                {t('calculette.demander')}
              </Button>
            </form>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}

function ResultatCalcul({ calcul, devise }: { calcul: CalculNegociation; devise: string }) {
  const { t, i18n } = useTranslation('presentation');
  const { galerie } = useShellContext();
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const champs = [
    'prixPublicCentimes',
    'prixProposeCentimes',
    'remiseCentimes',
    'totalHtCentimes',
    'totalTvaCentimes',
    'totalTtcCentimes',
    'droitDeSuiteCentimes',
  ] as const;
  const confidentiels = [
    'tvaSurMargeCentimes',
    'netArtisteCentimes',
    'netGalerieCentimes',
    'plancherEffectifCentimes',
  ] as const;
  return (
    <div className={calcul.sousPlancher ? styles.erreur : undefined}>
      {calcul.sousPlancher ? <p role="alert">{t('calculette.sousPlancher')}</p> : null}
      <dl className={styles.resultats} aria-live="polite">
        {champs.map((champ) => (
          <div key={champ}>
            <dt>{t(`calculette.${champ}`)}</dt>
            <dd>
              <Price
                className={styles.montant}
                cents={calcul[champ]}
                currency={devise}
                lang={langue}
              />
            </dd>
          </div>
        ))}
        <div>
          <dt>{t('calculette.pourcentage')}</dt>
          <dd>
            {new Intl.NumberFormat(langue, { style: 'percent', maximumFractionDigits: 2 }).format(
              calcul.remisePointsDeBase / 10000,
            )}
          </dd>
        </div>
        <div>
          <dt>{t('calculette.payeur')}</dt>
          <dd>
            {t(calcul.droitDeSuiteAChargeAcheteur ? 'calculette.acheteur' : 'calculette.galerie')}
          </dd>
        </div>
        {calcul.regimeEffectif ? (
          <div>
            <dt>{t('calculette.regime')}</dt>
            <dd>{t(`calculette.regimes.${calcul.regimeEffectif}`)}</dd>
          </div>
        ) : null}
        {galerieALaction(galerie, 'artworks:read-artist-price')
          ? confidentiels
              .filter((champ) => calcul[champ] !== undefined)
              .map((champ) => (
                <div key={champ}>
                  <dt>{t(`calculette.${champ}`)}</dt>
                  <dd>
                    {calcul[champ] === null ? (
                      t('calculette.nonRenseigne')
                    ) : (
                      <Price
                        className={styles.montant}
                        cents={calcul[champ]!}
                        currency={devise}
                        lang={langue}
                      />
                    )}
                  </dd>
                </div>
              ))
          : null}
      </dl>
    </div>
  );
}
